import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { userSelector } from '../user/userSlice';

import { toast } from 'react-toastify';
import api from '../../services/api';
import apiErrorFormatter from '../../services/apiErrorFormatter';

import { Formik } from 'formik';
import * as yup from 'yup';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EngineeringIcon from '@mui/icons-material/Engineering';

function CreateMaintenanceTask() {
  const { t } = useTranslation('maintenance');

  const { user } = useSelector(userSelector);
  const plantId = user?.plant?.id > 0 ? user?.plant?.id : 0;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showContent, setShowContent] = useState(false);

  const [areas, setAreas] = useState([]);
  const [lines, setLines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  // const [errorTypes, setErrorTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    setErrors([]);
    setShowContent(false);
    setAreas([]);
    setLines([]);
    setEquipments([]);
    // setErrorTypes([]);
    setPriorities([]);
    setMaintenanceTypes([]);
    let errors = [];
    await api
      .get('/maintenance/areas', {
        params: { isEnabled: true, plantId: plantId },
      })
      .then(
        (res) => {
          setAreas(res?.data);
        },
        (err) => {
          errors = apiErrorFormatter(err);
        }
      );
    await api
      .get('/maintenance/lines', {
        params: { isEnabled: true, plantId: plantId },
      })
      .then(
        (res) => {
          setLines(res?.data);
        },
        (err) => {
          errors = errors.concat(
            apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
          );
        }
      );
    await api
      .get('/maintenance/equipments', {
        params: { isEnabled: true, plantId: plantId },
      })
      .then(
        (res) => {
          setEquipments(res?.data);
        },
        (err) => {
          errors = errors.concat(
            apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
          );
        }
      );
    // await api.get('/maintenance/error-types').then(
    //   (res) => {
    //     setErrorTypes(res?.data);
    //   },
    //   (err) => {
    //     errors = errors.concat(
    //       apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
    //     );
    //   }
    // );
    await api.get('/maintenance/priorities?isEnabled=true').then(
      (res) => {
        setPriorities(res?.data);
      },
      (err) => {
        errors = errors.concat(
          apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
        );
      }
    );
    await api.get('/maintenance/maintenance-types?isEnabled=true').then(
      (res) => {
        setMaintenanceTypes(res?.data);
      },
      (err) => {
        errors = errors.concat(
          apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
        );
      }
    );
    if (errors.length === 0) setShowContent(true);
    else setErrors(apiErrorFormatter(errors));
    setLoading(false);
  };

  const onSubmit = async (values, { resetForm, validateForm }) => {
    setLoading(true);
    setErrors([]);
    await api.post('/maintenance/create-maintenance-task', values).then(
      (res) => {
        if (res.status === 200) {
          toast.success(t('maintenance_task_created'));
          resetForm();
          validateForm();
        }
      },
      (err) => {
        setErrors(apiErrorFormatter(err));
      }
    );
    setLoading(false);
  };

  return (
    <Container sx={{ mt: 1, mb: 2 }}>
      <Box mb={1}>
        <Divider>{t('create_maintenance_task')}</Divider>
      </Box>
      <Paper sx={{ p: 1, textAlign: 'center' }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            sx={{ px: 4, py: 2 }}
            disabled={loading}
            startIcon={
              !loading ? (
                <ArrowBackIcon />
              ) : (
                <CircularProgress color="inherit" size={20} />
              )
            }
            component={RouterLink}
            to=".."
            relative="path"
          >
            {t('common:back')}
          </Button>
        </Box>
      </Paper>
      {!showContent && loading ? (
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <CircularProgress size={'60px'} />
        </Box>
      ) : null}
      {!showContent && errors.length > 0 ? (
        <Box mt={5}>
          {errors
            .slice()
            .reverse()
            .map((error) => (
              <Box key={error} mt={1}>
                <Alert variant="outlined" severity="error">
                  {t(error)}
                </Alert>
              </Box>
            ))}
        </Box>
      ) : null}
      {showContent ? (
        <Paper
          sx={{ mt: 5, pt: 1, pb: 5, pl: 2, pr: 2, textAlign: 'center' }}
          variant="outlined"
        >
          <Formik
            validateOnMount
            initialValues={{
              areaId: '',
              lineId: '',
              equipmentId: '',
              errorTypeId: '',
              priorityId: '',
              maintenanceTypeId: '',
              comment: '',
            }}
            validationSchema={yup.object().shape({
              areaId: yup.string().required('common:required_field'),
              lineId: yup.string().required('common:required_field'),
              equipmentId: yup.string().required('common:required_field'),
              errorTypeId: yup.string().required('common:required_field'),
              priorityId: yup.string().required('common:required_field'),
              maintenanceTypeId: yup.string().required('common:required_field'),
              comment: yup.string(),
            })}
            onSubmit={(values, resetForm, validateForm) => {
              onSubmit(values, resetForm, validateForm);
            }}
          >
            {({
              isValid,
              dirty,
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              setFieldValue,
              validateField,
            }) => (
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                <Box mt={2}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="areaId"
                    name="areaId"
                    label={t('area')}
                    value={values.areaId}
                    onChange={(event) => {
                      setFieldValue('lineId', '');
                      validateField('lineId');
                      setFieldValue('equipmentId', '');
                      validateField('equipmentId');
                      setFieldValue('errorTypeId', '');
                      validateField('errorTypeId');
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    error={touched.areaId && Boolean(errors.areaId)}
                    helperText={touched.areaId && t(errors.areaId)}
                    disabled={loading}
                  >
                    {areas?.map((area) => (
                      <MenuItem key={area?.id} value={area?.id}>
                        {area?.areaName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box mt={2}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="lineId"
                    name="lineId"
                    label={t('line')}
                    value={values.lineId}
                    onChange={(event) => {
                      setFieldValue('equipmentId', '');
                      validateField('equipmentId');
                      setFieldValue('errorTypeId', '');
                      validateField('errorTypeId');
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    error={touched.lineId && Boolean(errors.lineId)}
                    helperText={touched.lineId && t(errors.lineId)}
                    disabled={loading || values.areaId <= 0}
                  >
                    {lines
                      ?.filter((line) => line?.areaId === values.areaId)
                      ?.map((line) => (
                        <MenuItem key={line?.id} value={line?.id}>
                          {line?.lineName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Box>
                <Box mt={2}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="equipmentId"
                    name="equipmentId"
                    label={t('equipment')}
                    value={values.equipmentId}
                    onChange={(event) => {
                      if (
                        !equipments
                          ?.find(
                            (equipment) => equipment?.id === event.target.value
                          )
                          ?.errorTypes?.find(
                            (errorType) => errorType?.id === values.errorTypeId
                          )
                      ) {
                        setFieldValue('errorTypeId', '');
                        validateField('errorTypeId');
                      }
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    error={touched.equipmentId && Boolean(errors.equipmentId)}
                    helperText={touched.equipmentId && t(errors.equipmentId)}
                    disabled={
                      loading || values.lineId <= 0 || values.areaId <= 0
                    }
                  >
                    {equipments
                      ?.filter(
                        (equipment) => equipment?.lineId === values.lineId
                      )
                      ?.map((equipment) => (
                        <MenuItem key={equipment?.id} value={equipment?.id}>
                          {equipment?.equipmentName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Box>
                <Box mt={2}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="errorTypeId"
                    name="errorTypeId"
                    label={t('error_type')}
                    value={values.errorTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.errorTypeId && Boolean(errors.errorTypeId)}
                    helperText={touched.errorTypeId && t(errors.errorTypeId)}
                    disabled={
                      loading ||
                      values.lineId <= 0 ||
                      values.areaId <= 0 ||
                      values.equipmentId <= 0
                    }
                  >
                    {equipments?.find(
                      (equipment) => equipment?.id === values.equipmentId
                    )?.errorTypes?.length > 0
                      ? equipments
                          ?.find(
                            (equipment) => equipment?.id === values.equipmentId
                          )
                          ?.errorTypes?.map((errorType) => (
                            <MenuItem key={errorType?.id} value={errorType?.id}>
                              {t(errorType?.errorTypeName)}
                            </MenuItem>
                          ))
                      : []?.map((errorType) => (
                          <MenuItem key={errorType?.id} value={errorType?.id}>
                            {t(errorType?.errorTypeName)}
                          </MenuItem>
                        ))}
                    {/* {errorTypes?.map((errorType) => (
                      <MenuItem key={errorType?.id} value={errorType?.id}>
                        {t(errorType?.errorTypeName)}
                      </MenuItem>
                    ))} */}
                  </TextField>
                </Box>
                <Box mt={4}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="priorityId"
                    name="priorityId"
                    label={t('priority')}
                    value={values.priorityId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.priorityId && Boolean(errors.priorityId)}
                    helperText={touched.priorityId && t(errors.priorityId)}
                    disabled={loading}
                  >
                    {priorities?.map((priority) => (
                      <MenuItem key={priority?.id} value={priority?.id}>
                        {t(priority?.priorityName)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box mt={2}>
                  <TextField
                    fullWidth
                    select
                    required
                    sx={{ textAlign: 'left' }}
                    id="maintenanceTypeId"
                    name="maintenanceTypeId"
                    label={t('maintenance_type')}
                    value={values.maintenanceTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.maintenanceTypeId &&
                      Boolean(errors.maintenanceTypeId)
                    }
                    helperText={
                      touched.maintenanceTypeId && t(errors.maintenanceTypeId)
                    }
                    disabled={loading}
                  >
                    {maintenanceTypes?.map((maintenanceType) => (
                      <MenuItem
                        key={maintenanceType?.id}
                        value={maintenanceType?.id}
                      >
                        {t(maintenanceType?.maintenanceTypeName)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box mt={4}>
                  <TextField
                    fullWidth
                    multiline
                    id="comment"
                    name="comment"
                    label={t('common:comment')}
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.comment && Boolean(errors.comment)}
                    helperText={touched.comment && t(errors.comment)}
                    disabled={loading}
                  />
                </Box>
                <Box mt={8}>
                  {user?.roles.some(
                    (role) => role === 'admin' || role === 'production'
                  ) ? (
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      sx={{
                        py: 2,
                        color: 'primary.contrastText',
                        backgroundColor: 'success.main',
                        ':hover': {
                          color: 'primary.contrastText',
                          backgroundColor: 'success.dark',
                        },
                      }}
                      fullWidth
                      disabled={!(isValid && dirty) || loading}
                      startIcon={
                        !loading ? (
                          <EngineeringIcon />
                        ) : (
                          <CircularProgress color="inherit" size={20} />
                        )
                      }
                    >
                      {t('common:create')}
                    </Button>
                  ) : (
                    <Box>
                      <Alert variant="outlined" severity="error">
                        {t('common:unauthorized')}
                      </Alert>
                    </Box>
                  )}
                </Box>
              </form>
            )}
          </Formik>
          {errors.length > 0 ? (
            <Box mt={5}>
              {errors
                .slice()
                .reverse()
                .map((error) => (
                  <Box key={error} mt={1}>
                    <Alert variant="outlined" severity="error">
                      {t(error)}
                    </Alert>
                  </Box>
                ))}
            </Box>
          ) : null}
        </Paper>
      ) : null}
      {/* </Container> */}
    </Container>
  );
}

export default CreateMaintenanceTask;
